import React, { useEffect, useReducer } from 'react';
import { Row, Col, Form, FormGroup, FormControl, Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { withHeaderMessages } from '../higherOrderComponents/WithHeaderMessages';
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from '../lib/components/LoadingSpinner';

const OnDemandReportRequest = (props) => {

    const initialState = {
        reportRequestSubmitted: false,
        isLoading: true,
        isOutputFormatsLoading: true,
        isProfileLoading: true,
        reportTypes: [],
        outputFormats: [],
        customers: [],
        providers: [],
        reportId: { value: '', required: true, touched: false, invalid: true, message: '' },
        customerCode: { value: '', required: true, touched: false, invalid: true, message: '' },
        providerCode: { value: '', required: false, touched: false, invalid: true, message: '' },
        outputFormat: { value: '', required: true, touched: false, invalid: true, message: '' },
        report: {},
        parameters: {},
        parameterSets: {},
        fileUploads: {},
        fileTypeUploadErrors: []
    }

    const reducer = (state, action) => {
        switch (action.type) {
            case 'idBlurred':
                return {
                    ...state,
                    [action.payload]: {
                        ...state[action.payload],
                        touched: true
                    }
                }
            case 'reportRequestSubmitted':
                return {
                    ...state,
                    reportRequestSubmitted: action.payload
                }
            case 'reportTypes':
                return {
                    ...state,
                    reportTypes: action.payload,
                    isLoading: false
                }
            case 'outputFormats':
                return {
                    ...state,
                    outputFormats: action.payload,
                    isOutputFormatsLoading: false
                }
            case 'reportId':
                const report = state.reportTypes.find(r => r.reportId == action.payload);
                return {
                    ...state,
                    parameters: {},
                    parameterSets: {},
                    reportId: { ...state.reportId,
                        value: action.payload },
                    report: report
                }
            case 'customers': {
                const customers = action.payload;
                const customerCode = customers.length === 1 ? customers[0].code : "";
                const customer = customers.find(c => c.code === customerCode);
                const providers = customer ? customer.providers : [];
                const providerCode = (customer && customer.providers && customer.providers.length === 1) ? customer.providers[0].code : "";

                return {
                    ...state,
                    customers: customers,
                    customerCode: { ...state.customerCode,
                        value: customerCode },
                    providerCode: { ...state.providerCode,
                        value: providerCode },
                    providers: providers,
                    isProfileLoading: false
                }
            }
            case 'customerCode': {
                const customer = state.customers.find(c => c.code === action.payload);
                const providers = customer ? customer.providers : [];
                const providerCode = (customer && customer.providers && customer.providers.length === 1) ? customer.providers[0].code : "";

                return {
                    ...state,
                    customerCode: { ...state.customerCode,
                        value: action.payload },
                    providerCode: { ...state.providerCode,
                        value: providerCode },
                    providers: providers
                }
            }
            case 'providerCode':
                return {
                    ...state,
                    providerCode: { ...state.providerCode,
                        value: action.payload },
                }
            case 'outputFormat':
                return {
                    ...state,
                    outputFormat: { ...state.outputFormat,
                        value: action.payload }
                }
            case 'parameters':
                let parameters = state.parameters
                let parameterSets = state.parameterSets
                if (action.checked === undefined) {
                    parameters[action.name] = action.payload;
                } else {
                    if (parameterSets[action.name] === undefined) {
                        parameterSets[action.name] = new Set();
                    }
                    if (action.checked === true) {
                        parameterSets[action.name].add(action.payload);
                    } else {
                        parameterSets[action.name].delete(action.payload);
                    }
                    parameters[action.name] = Array.from(parameterSets[action.name]).join(',');
                }
                return {
                    ...state,
                    parameters: parameters,
                }
            case 'isLoading':
                return {
                    ...state,
                    isLoading: action.payload
                }
            case 'isOutputFormatsLoading':
                return {
                    ...state,
                    isOutputFormatsLoading: action.payload
                }
            case 'isProfileLoading':
                return {
                    ...state,
                    isProfileLoading: action.payload
                }
            case 'hideLoader':
                return {
                    ...state,
                    isLoading: false
                }
            default:
                throw new Error();
        }
    }

    const [uploadState, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        async function getReports() {
            props.header.setHeader("reports");
            await props.api.get(`/api/on-demand-report/types`)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                })
                .then(data => {
                    dispatch({ type: 'reportTypes', payload: data });
                })
                .catch(hideLoader)
                .then(hideLoader);
        }
        getReports();
    }, [])

    useEffect(() => {
        async function getOutputFormats() {
            await props.api.get(`/api/on-demand-report/output-types`)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                })
                .then(data => {
                    dispatch({ type: 'outputFormats', payload: data });
                })
                .catch(dispatch({ type: 'isOutputFormatsLoading', payload: false }))
                .then(dispatch({ type: 'isOutputFormatsLoading', payload: false }));
        }
        getOutputFormats();
    }, [])

    useEffect(() => {
        async function getCustomerProfile() {
            props.api.get('/api/customer/profile')
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                })
                .then(data => {
                    dispatch({ type: 'customers', payload: data });
                })
                .catch(dispatch({ type: 'isProfileLoading', payload: false }))
                .then(dispatch({ type: 'isProfileLoading', payload: false }));
        }
        getCustomerProfile();
    }, [])

    const reportRequestIsValid = () => {
        const { parameters, report, reportId, outputFormat, customerCode,
            providerCode, customers } = uploadState;

        return outputFormat.value
            && customerCode.value
            && reportId.value
            && (providerCode.value || customers.every(c => c.limitedProviders === false))
            && report.parameters && report.parameters.every(p => parameters[p.name]);
    };

    const handleReportRequestSubmitted = async (event) => {
        dispatch({ type: 'isLoading', payload: true });
        const { parameters, reportId, outputFormat } = uploadState;
        async function postRequest() {
            const postBody = {
                customerCode: customerCode.value,
                providerCode: providerCode.value,
                reportId: reportId.value,
                outputFormat: outputFormat.value,
                parameters: parameters
            }

            await props.api.post('/api/on-demand-report', postBody)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                })
                .then(data => {
                    dispatch({ type: 'reportRequestSubmitted', payload: true });
                })
                .catch(hideLoader)
                .finally(hideLoader);
        }
        await postRequest();
    };

    const handleReportChanged = (event) => {
        dispatch({ type: 'reportId', payload: event.target.value });
    }

    const handleOutputFormatChanged = (event) => {
        dispatch({ type: 'outputFormat', payload: event.target.value });
    }

    const hideLoader = () => {
        dispatch({ type: 'isLoading', payload: false });
    };

    const resetSubmitted = () => {
        dispatch({ type: 'reportRequestSubmitted', payload: false });
    };

    const handleCustomerChanged = (event, name) => {
        dispatch({ type: 'customerCode', payload: event.target.value });
    }

    const handleProviderChanged = (event, name) => {
        dispatch({ type: 'providerCode', payload: event.target.value });
    }

    const handleParameterChanged = (event, name) => {
        dispatch({ type: 'parameters', payload: event.target.value, name: name });
    }

    const handleMultiSelectParameterChanged = (event, name, description) => {
        dispatch({ type: 'idBlurred', payload: name });
        dispatch({ type: 'parameters', payload: description, name: name, checked: event.target.checked });
    }

    const handleBlur = (event) => {
        const { id } = event.target;
        dispatch({ type: 'idBlurred', payload: id });
    }

    const shouldMarkError = (id) => {
        const field = uploadState[id];
        const result = field !== undefined && field.value === '' && field.touched;
        return result;
    }
    
    const shouldMarkProviderError = () => {
        const providerCode = uploadState.providerCode;
        const limitedProviders = uploadState.customers.some(c => c.limitedProviders === true);
        const result = providerCode !== undefined && providerCode.value === '' && providerCode.touched &&
            limitedProviders;
        return result;
    }
    
    const shouldMarkParameterError = (id) => {
        const field = uploadState[id];
        const parameter = uploadState.parameters[id];
        const result = field !== undefined && field.touched &&
            (parameter === undefined || parameter === '');
        return result;
    }

    const renderDate = (parameter) => {
        return (
            <FormGroup key={parameter.name}>
                <Row className="mt-3">
                    <Col sm={12} lg={4} xl={3}>
                        <Form.Label className="mt-1">{parameter.friendlyName}</Form.Label>
                    </Col>
                    <Col lg={7} xl={7} className="text-start p-0">
                        <FormControl
                            type="date"
                            id={parameter.name}
                            name={parameter.name}
                            placeholder={parameter.name}
                            onChange={(event) => handleParameterChanged(event, parameter.name)}
                            onBlur={handleBlur}
                            isInvalid={shouldMarkParameterError(parameter.name) ? " error" : null} />
                    </Col>
                </Row>
            </FormGroup>
        )
    }

    const renderLookup = (parameter) => {
        return (
            <FormGroup key={parameter.name}>
                <Row className="mt-3">
                    <Col sm={12} lg={4} xl={3}>
                        <Form.Label className="mt-1">{parameter.friendlyName}</Form.Label>
                    </Col>
                    <Col lg={7} xl={7} className="text-start p-0">
                        <Form.Select
                            value={parameters[parameter.name]}
                            className="select"
                            id={parameter.name}
                            name={parameter.name}
                            onChange={(event) => handleParameterChanged(event, parameter.name)}
                            onBlur={handleBlur}
                            isInvalid={shouldMarkParameterError(parameter.name) ? " error" : null}
                        >
                            <option value=''>{" "}</option>
                            {parameter.lookupValues && parameter.lookupValues.map((lv, j) =>
                                <option value={lv.value} key={j}>{lv.description}</option>
                            )}
                        </Form.Select>
                    </Col>
                </Row>
            </FormGroup>
        )
    }

    const renderMultiSelect = (parameter) => {
        return (
            <FormGroup key={parameter.name} >
                <Row className="mt-3">
                    <Col sm={12} lg={4} xl={3}>
                        <Form.Label className="mt-1">{parameter.friendlyName}</Form.Label>
                    </Col>
                    <Col lg={7} xl={7} className="text-start p-0" style={{ marginLeft: "20px" }}>
                        {parameter.lookupValues && parameter.lookupValues.map((lv, j) =>
                            <Form.Check
                                key={`${parameter.name}${j}`}
                                id={lv.description}
                                name={lv.description}
                                label={lv.description}
                                type="checkbox"
                                style={{ padding: "0", margin: "0", minHeight: "15px" }}
                                onChange={(event) => handleMultiSelectParameterChanged(event, parameter.name, lv.description)}
                            ></Form.Check>
                        )}
                        {(shouldMarkParameterError(parameter.name)) &&
                            <div class="text-danger">
                                Please select at least one value.
                            </div>
                        }
                    </Col>
                </Row>
            </FormGroup>
        )
    }

    const { reportRequestSubmitted, customerCode, providerCode, reportId, outputFormat,
        reportTypes, customers, providers, outputFormats, report, parameters } = uploadState;

    if (uploadState.isLoading || uploadState.isOutputFormatsLoading || uploadState.isProfileLoading) {
        return <LoadingSpinner />;
    }
    if (reportRequestSubmitted) {
        return (
            <div className="section container">
                <Row className="section border-between">
                    <Col sm={12} className="contract-details">
                        <h2 style={{ marginTop: "30px", marginBottom: "20px" }}>
                            We received your submission.
                        </h2>
                        <p>
                            We have started processing your reporting request. An email will be sent to you directly when the report is ready. When ready, your report will show on the <Link to="/reports">Reports</Link> page.
                        </p>
                        <p>
                            Need to request another report? <a className="cursor" onClick={(event) => resetSubmitted()}>Click here</a>.
                        </p>
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <>
            <Container>
                <Col sm={10}>
                    <Form onSubmit={handleReportRequestSubmitted} className="processing-form">
                        <div className="card processing-doc-type border border-0">
                            <div className="card-body">
                                <h3>Request On Demand Report</h3>
                                {(customers && customers.length > 1) &&
                                    <FormGroup key="customer">
                                        <Row className="mt-3">
                                            <Col sm={12} lg={4} xl={3}>
                                                <Form.Label className="mt-1">Customer</Form.Label>
                                            </Col>
                                            <Col lg={7} xl={7} className="text-start p-0">
                                                <Form.Select
                                                    value={customerCode.value}
                                                    className="select"
                                                    id="customerCode"
                                                    name="customerCode"
                                                    onChange={handleCustomerChanged}
                                                    onBlur={handleBlur}
                                                    isInvalid={shouldMarkError("customerCode") ? " error" : null}
                                                >
                                                    <option value=''>{" "}</option>
                                                    {customers && customers.map((c, i) =>
                                                        <option value={c.code} key={i}>{c.name}</option>
                                                    )}
                                                </Form.Select>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                }
                                {(providers && providers.length > 1) &&
                                    <FormGroup key="provider">
                                        <Row className="mt-3">
                                            <Col sm={12} lg={4} xl={3}>
                                                <Form.Label className="mt-1">Provider</Form.Label>
                                            </Col>
                                            <Col lg={7} xl={7} className="text-start p-0">
                                                <Form.Select
                                                    value={providerCode.value}
                                                    className="select"
                                                    id="providerCode"
                                                    name="providerCode"
                                                    onBlur={handleBlur}
                                                    onChange={handleProviderChanged}
                                                    isInvalid={shouldMarkProviderError() ? " error" : null}
                                                >
                                                    <option value=''>{" "}</option>
                                                    {providers &&
                                                        providers
                                                            .sort(function (a, b) {
                                                                if (a.name < b.name) {
                                                                    return -1;
                                                                }
                                                                if (a.name > b.name) {
                                                                    return 1;
                                                                }
                                                                return 0;
                                                            })
                                                            .map((p, i) =>
                                                                <option value={p.code} key={i}>{p.name}</option>
                                                            )}
                                                </Form.Select>
                                            </Col>
                                        </Row>
                                    </FormGroup>
                                }
                                <FormGroup key="report">
                                    <Row className="mt-3">
                                        <Col sm={12} lg={4} xl={3}>
                                            <Form.Label className="mt-1">Report</Form.Label>
                                        </Col>
                                        <Col lg={7} xl={7} className="text-start p-0">
                                            <Form.Select
                                                value={reportId.value}
                                                className="select"
                                                id="reportId"
                                                name="reportId"
                                                onBlur={handleBlur}
                                                onChange={handleReportChanged}
                                                isInvalid={shouldMarkError("reportId") ? " error" : null}
                                            >
                                                <option value=''>{" "}</option>
                                                {reportTypes && reportTypes.map((r, i) =>
                                                    <option value={r.reportId} key={i}>{r.friendlyName}</option>
                                                )}
                                            </Form.Select>
                                        </Col>
                                    </Row>
                                </FormGroup>
                                {report && report.parameters && report.parameters.map((p, i) => {
                                    return (
                                        p.fieldType === "Date"
                                            ? renderDate(p)
                                            : p.fieldType === "Lookup"
                                                ? renderLookup(p)
                                                : p.fieldType === "MultiSelect"
                                                    ? renderMultiSelect(p)
                                                    : ""
                                    )
                                })}
                                <FormGroup key="outputFormat">
                                    <Row className="mt-3">
                                        <Col sm={12} lg={4} xl={3}>
                                            <Form.Label className="mt-1">Output Format</Form.Label>
                                        </Col>
                                        <Col lg={7} xl={7} className="text-start p-0">
                                            <Form.Select
                                                value={outputFormat.value}
                                                className="select"
                                                id="outputFormat"
                                                name="outputFormat"
                                                onBlur={handleBlur}
                                                onChange={handleOutputFormatChanged}
                                                isInvalid={shouldMarkError("outputFormat") ? " error" : null}
                                            >
                                                <option value=''>{" "}</option>
                                                {outputFormats && outputFormats.map((t, i) =>
                                                    <option value={t} key={i}>{t}</option>
                                                )}
                                            </Form.Select>
                                        </Col>
                                    </Row>
                                </FormGroup>
                                <button type="submit" className="btn btn-primary cursor" style={{ marginTop: "5px" }}
                                    disabled={!reportRequestIsValid()}>Submit</button>
                            </div>
                        </div>
                    </Form>
                </Col>
            </Container>
        </>
    );
}

export default withHeaderMessages(WithApiAccess(OnDemandReportRequest));
